import { useState, useEffect } from 'react'
import { useAuthStore } from '@/stores/auth.store'
import { toast } from 'sonner'
import { HedgePairView } from '@/components/Cycles/HedgePairView'

interface CycleUser {
  user_id: number
  email: string
  name: string
  total_pairs: number
  active_pairs: number
  total_ops: number
  closed_ops: number
  open_ops: number
}

interface CyclePair {
  pair_id: number
  display_name: string
  prop_size: number
  prop_phase: string
  prop_provider: string
  is_active: boolean
  created_at: string | null
  equity: number | null
  balance: number | null
  ea_last_seen: string | null
  cycle_pnl: number | null
  cycle_pnl_pct: number | null
  total_ops: number
  closed_ops: number
  open_ops: number
  first_op: string | null
  last_close: string | null
}

interface CycleOperation {
  id: number
  direction: string
  status: string
  prop_open_price: number | null
  broker_open_price: number | null
  prop_close_price: number | null
  broker_close_price: number | null
  prop_lots: number | null
  broker_lots: number | null
  sl_usd: number | null
  tp_usd: number | null
  prop_pnl: number | null
  broker_pnl: number | null
  total_pnl: number | null
  prop_equity_at_open: number | null
  prop_equity_at_close: number | null
  signal_source: string
  created_at: string | null
  closed_at: string | null
  prop_ticket: number | null
  broker_ticket: number | null
}

interface CycleDetail {
  pair: {
    id: number
    display_name: string
    prop_size: number
    prop_phase: string
    prop_provider: string
    is_active: boolean
    created_at: string | null
  }
  snapshot: {
    equity: number | null
    balance: number | null
    ea_last_seen: string | null
    cycle_pnl: number | null
    cycle_pnl_pct: number | null
  }
  stats: {
    total_ops: number
    closed_ops: number
    open_ops: number
    by_source: Record<string, number>
    by_direction: Record<string, number>
  }
  operations: CycleOperation[]
}

type View = 'users' | 'pairs' | 'detail'

const PROVIDER_LABELS: Record<string, string> = {
  FND: 'FundedNext',
  FPR: 'FunderPro',
  FPI: 'FundingPips',
}

const PHASE_LABELS: Record<string, string> = {
  challenge: 'Challenge',
  real_pre_payout: 'Real (Pre-Payout)',
  real_post_payout: 'Real (Post-Payout)',
}

const SOURCE_LABELS: Record<string, string> = {
  manual_direct: 'Manuale',
  yomi_manual: 'Yomi (manuale)',
  yomi_auto: 'Yomi (auto)',
  jifu_auto: 'Sempai (auto)',
  jifu_manual: 'Sempai (manuale)',
  test_hedge: 'Test',
  unknown: 'Non specificato',
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: '2-digit' }) +
    ' ' + d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
}

function fmtMoney(v: number | null, decimals = 2): string {
  if (v == null) return '—'
  return v.toLocaleString('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

function PnlBadge({ value, suffix = '' }: { value: number | null; suffix?: string }) {
  if (value == null) return <span className="text-[#64748b]">—</span>
  const isPos = value > 0
  const isNeg = value < 0
  const color = isPos ? 'text-emerald-400' : isNeg ? 'text-rose-400' : 'text-[#94a3b8]'
  const sign = isPos ? '+' : ''
  return <span className={`font-bold ${color}`}>{sign}{fmtMoney(value)}{suffix}</span>
}

export function IxenCycles() {
  const { token } = useAuthStore()
  const [view, setView] = useState<View>('users')
  const [loading, setLoading] = useState(false)

  const [users, setUsers] = useState<CycleUser[]>([])
  const [selectedUser, setSelectedUser] = useState<CycleUser | null>(null)
  const [pairs, setPairs] = useState<CyclePair[]>([])
  const [detail, setDetail] = useState<CycleDetail | null>(null)

  const headers = { Authorization: `Bearer ${token}` }

  async function fetchUsers() {
    setLoading(true)
    try {
      const r = await fetch('/api/v1/admin/ixen-cycles/users', { headers })
      if (!r.ok) throw new Error(`${r.status}`)
      setUsers(await r.json())
      setView('users')
    } catch (e: any) {
      toast.error('Errore caricamento utenti', { description: e.message })
    } finally {
      setLoading(false)
    }
  }

  async function fetchPairs(user: CycleUser) {
    setLoading(true)
    setSelectedUser(user)
    try {
      const r = await fetch(`/api/v1/admin/ixen-cycles/users/${user.user_id}/pairs`, { headers })
      if (!r.ok) throw new Error(`${r.status}`)
      setPairs(await r.json())
      setView('pairs')
    } catch (e: any) {
      toast.error('Errore caricamento coppie', { description: e.message })
    } finally {
      setLoading(false)
    }
  }

  async function fetchDetail(pairId: number) {
    setLoading(true)
    try {
      const r = await fetch(`/api/v1/admin/ixen-cycles/pairs/${pairId}/detail`, { headers })
      if (!r.ok) throw new Error(`${r.status}`)
      setDetail(await r.json())
      setView('detail')
    } catch (e: any) {
      toast.error('Errore caricamento dettaglio', { description: e.message })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchUsers() }, [])

  function Breadcrumb() {
    return (
      <div className="flex items-center gap-1.5 text-[12px] text-[#94a3b8] mb-4">
        <button onClick={fetchUsers} className="hover:text-white transition-colors">Utenti</button>
        {(view === 'pairs' || view === 'detail') && selectedUser && (
          <>
            <span className="text-[#475569]">/</span>
            <button onClick={() => fetchPairs(selectedUser)} className="hover:text-white transition-colors">
              {selectedUser.email}
            </button>
          </>
        )}
        {view === 'detail' && detail && (
          <>
            <span className="text-[#475569]">/</span>
            <span className="text-white font-bold">{detail.pair.display_name}</span>
          </>
        )}
      </div>
    )
  }

  function UsersView() {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-[15px] font-bold text-white">Utenti iXen System ({users.length})</h2>
          <button onClick={fetchUsers} className="text-[11px] text-[#64748b] hover:text-white transition-colors">
            Aggiorna
          </button>
        </div>
        <div className="rounded-xl border border-[#1e3a5f]/60 overflow-hidden">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-[#0d1529] text-[#64748b] uppercase text-[10px] tracking-wider">
                <th className="text-left px-4 py-2.5">Utente</th>
                <th className="text-center px-3 py-2.5">Coppie</th>
                <th className="text-center px-3 py-2.5">Attive</th>
                <th className="text-center px-3 py-2.5">Operazioni</th>
                <th className="text-center px-3 py-2.5">Chiuse</th>
                <th className="text-center px-3 py-2.5">Aperte</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr
                  key={u.user_id}
                  onClick={() => fetchPairs(u)}
                  className="border-t border-[#1e293b] hover:bg-[#0f1d35] cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="text-white font-bold">{u.email}</div>
                    {u.name && <div className="text-[#64748b] text-[10px]">{u.name}</div>}
                  </td>
                  <td className="text-center px-3 text-[#cbd5e1]">{u.total_pairs}</td>
                  <td className="text-center px-3">
                    <span className="text-emerald-400 font-bold">{u.active_pairs}</span>
                  </td>
                  <td className="text-center px-3 text-[#cbd5e1] font-bold">{u.total_ops}</td>
                  <td className="text-center px-3 text-[#94a3b8]">{u.closed_ops}</td>
                  <td className="text-center px-3">
                    {u.open_ops > 0 ? <span className="text-amber-400 font-bold">{u.open_ops}</span> : <span className="text-[#475569]">0</span>}
                  </td>
                  <td className="px-3 text-right text-[#475569]">→</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  function PairsView() {
    if (!selectedUser) return null
    return (
      <div className="space-y-3">
        <h2 className="text-[15px] font-bold text-white">
          Coppie di {selectedUser.email} ({pairs.length})
        </h2>
        <div className="grid gap-3">
          {pairs.map(p => (
            <div
              key={p.pair_id}
              onClick={() => fetchDetail(p.pair_id)}
              className={`rounded-xl border p-4 cursor-pointer transition-all hover:border-blue-500/50 ${
                p.is_active
                  ? 'border-[#1e3a5f]/60 bg-gradient-to-br from-[#0c1d3a] to-[#0a1628]'
                  : 'border-[#1e293b] bg-[#0a1020] opacity-70'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${p.is_active ? 'bg-emerald-400' : 'bg-[#475569]'}`} />
                  <span className="text-white font-bold text-[13px]">{p.display_name}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#1e293b] text-[#94a3b8]">
                    {PROVIDER_LABELS[p.prop_provider] || p.prop_provider}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#1e293b] text-[#94a3b8]">
                    {PHASE_LABELS[p.prop_phase] || p.prop_phase}
                  </span>
                </div>
                <span className="text-[#475569] text-sm">→</span>
              </div>
              <div className="grid grid-cols-5 gap-3 text-[11px]">
                <div>
                  <span className="text-[#64748b] block text-[9px] uppercase tracking-wider font-bold">Capitale</span>
                  <span className="text-white font-bold">${fmtMoney(p.prop_size, 0)}</span>
                </div>
                <div>
                  <span className="text-[#64748b] block text-[9px] uppercase tracking-wider font-bold">Equity</span>
                  <span className="text-white font-bold">{p.equity != null ? `$${fmtMoney(p.equity)}` : '—'}</span>
                </div>
                <div>
                  <span className="text-[#64748b] block text-[9px] uppercase tracking-wider font-bold">P&L Ciclo</span>
                  <PnlBadge value={p.cycle_pnl} />
                  {p.cycle_pnl_pct != null && (
                    <span className={`text-[9px] ml-1 ${p.cycle_pnl_pct >= 0 ? 'text-emerald-400/60' : 'text-rose-400/60'}`}>
                      ({p.cycle_pnl_pct > 0 ? '+' : ''}{p.cycle_pnl_pct}%)
                    </span>
                  )}
                </div>
                <div>
                  <span className="text-[#64748b] block text-[9px] uppercase tracking-wider font-bold">Operazioni</span>
                  <span className="text-white font-bold">{p.total_ops}</span>
                  <span className="text-[#64748b] text-[9px] ml-1">({p.open_ops} aperte)</span>
                </div>
                <div>
                  <span className="text-[#64748b] block text-[9px] uppercase tracking-wider font-bold">Periodo</span>
                  <span className="text-[#94a3b8] text-[10px]">
                    {p.first_op ? fmtDate(p.first_op).split(' ')[0] : '—'}
                    {p.last_close ? ` → ${fmtDate(p.last_close).split(' ')[0]}` : ''}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  function DetailView() {
    if (!detail) return null
    const { pair, snapshot, stats, operations } = detail

    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-[#1e3a5f]/60 bg-gradient-to-br from-[#0c1d3a] to-[#0a1628] p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-[17px] font-black text-white">{pair.display_name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#1e293b] text-[#94a3b8]">
                  {PROVIDER_LABELS[pair.prop_provider] || pair.prop_provider}
                </span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#1e293b] text-[#94a3b8]">
                  {PHASE_LABELS[pair.prop_phase] || pair.prop_phase}
                </span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                  pair.is_active ? 'bg-emerald-900/40 text-emerald-400 border border-emerald-700/50' : 'bg-[#1e293b] text-[#64748b]'
                }`}>
                  {pair.is_active ? 'ATTIVA' : 'INATTIVA'}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[28px] font-black leading-none">
                <PnlBadge value={snapshot.cycle_pnl} />
              </div>
              {snapshot.cycle_pnl_pct != null && (
                <div className={`text-[12px] font-bold mt-0.5 ${snapshot.cycle_pnl_pct >= 0 ? 'text-emerald-400/70' : 'text-rose-400/70'}`}>
                  {snapshot.cycle_pnl_pct > 0 ? '+' : ''}{snapshot.cycle_pnl_pct}% del capitale
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-3 border-t border-[#1e3a5f]/40">
            <div>
              <span className="text-[#64748b] block text-[9px] uppercase tracking-wider font-bold mb-0.5">Capitale iniziale</span>
              <span className="text-white font-bold text-[14px]">${fmtMoney(pair.prop_size, 0)}</span>
            </div>
            <div>
              <span className="text-[#64748b] block text-[9px] uppercase tracking-wider font-bold mb-0.5">Ultimo EA</span>
              <span className="text-[#94a3b8] text-[11px]">{fmtDate(snapshot.ea_last_seen)}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl border border-[#1e293b] bg-[#0d1529] p-4">
            <span className="text-[#64748b] text-[10px] uppercase tracking-wider font-bold">Operazioni</span>
            <div className="mt-1 text-[22px] font-black text-white">{stats.total_ops}</div>
            <div className="text-[10px] text-[#64748b] mt-1">
              {stats.closed_ops} chiuse · {stats.open_ops} aperte
            </div>
          </div>
          <div className="rounded-xl border border-[#1e293b] bg-[#0d1529] p-4">
            <span className="text-[#64748b] text-[10px] uppercase tracking-wider font-bold">Per direzione</span>
            <div className="mt-2 space-y-1">
              {Object.entries(stats.by_direction).map(([d, c]) => (
                <div key={d} className="flex items-center justify-between text-[11px]">
                  <span className={d === 'BUY' ? 'text-sky-400 font-bold' : 'text-rose-400 font-bold'}>{d}</span>
                  <span className="text-[#94a3b8]">{c}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-[#1e293b] bg-[#0d1529] p-4">
            <span className="text-[#64748b] text-[10px] uppercase tracking-wider font-bold">Per fonte</span>
            <div className="mt-2 space-y-1">
              {Object.entries(stats.by_source).map(([s, c]) => (
                <div key={s} className="flex items-center justify-between text-[11px]">
                  <span className="text-[#94a3b8]">{SOURCE_LABELS[s] || s}</span>
                  <span className="text-white font-bold">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Vista hedge accoppiata: snapshot live + operazioni PROP/BROKER affiancate + totali */}
        <HedgePairView
          operations={operations}
          equity={snapshot.equity}
          balance={snapshot.balance}
          cyclePnl={snapshot.cycle_pnl}
        />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="mb-5">
        <h1 className="text-[22px] font-black text-white">Bilancio Cicli Prop</h1>
        <p className="text-[12px] text-[#64748b]">Monitoraggio operazioni e P&L per coppia iXen System</p>
      </div>

      <Breadcrumb />

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="ml-3 text-[#94a3b8] text-[13px]">Caricamento...</span>
        </div>
      ) : (
        <>
          {view === 'users' && <UsersView />}
          {view === 'pairs' && <PairsView />}
          {view === 'detail' && <DetailView />}
        </>
      )}
    </div>
  )
}
